import { call, put, takeLatest } from 'redux-saga/effects';
import { message } from 'antd';
import * as types from '../constants/actionTypes';
import { productsAPI, auditLogsAPI } from '../../api/config';
import {
  fetchProductsSuccess,
  fetchProductsFailure,
  createProductSuccess,
  createProductFailure,
  updateProductSuccess,
  updateProductFailure,
  getProductSuccess,
  getProductFailure,
  fetchProductFormDataSuccess,
  fetchProductFormDataFailure,
} from '../actions/productActions';

function* fetchProductsSaga() {
  try {
    const response = yield call(productsAPI.fetchProducts);
    // New API returns { products: [...], categories: [...], units: [...], tax_slabs: [...], count: number }
    yield put(fetchProductsSuccess(response.data));
  } catch (error) {
    const errorMessage = error.message || 'Failed to fetch products';
    yield put(fetchProductsFailure(errorMessage));
    message.error(errorMessage);
  }
}

function* fetchProductFormDataSaga() {
  try {
    const response = yield call(productsAPI.fetchProducts);
    // Extract form data from the API response
    const formData = {
      categories: response.data.categories || [],
      units: response.data.units || [],
      tax_slabs: response.data.tax_slabs || [],
      vendors: response.data.vendors || []
    };
    yield put(fetchProductFormDataSuccess(formData));
  } catch (error) {
    const errorMessage = error.message || 'Failed to load product form data';
    yield put(fetchProductFormDataFailure(errorMessage));
    message.error(errorMessage);
  }
}

function* createProductSaga(action) {
  try {
    const response = yield call(productsAPI.createProduct, action.payload);
    // New API returns { message: "...", product: {...} }
    const product = response.data.product || response.data;
    yield put(createProductSuccess(product));
    message.success(response.data.message || 'Product created successfully');
    // Log product creation
    yield call(auditLogsAPI.createAuditLog, {
      entityType: 'product',
      entityId: product.id,
      changeType: 'create',
      newData: product
    });
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to create product';
    yield put(createProductFailure(errorMessage));
    message.error(errorMessage);
  }
}

function* updateProductSaga(action) {
  try {
    const { id, productData } = action.payload;
    const oldProductResponse = yield call(productsAPI.getProduct, id);
    const oldProduct = oldProductResponse.data.product || oldProductResponse.data;
    const response = yield call(productsAPI.updateProduct, id, productData);
    const updatedProduct = response.data.product || response.data;
    yield put(updateProductSuccess(updatedProduct));
    message.success(response.data.message || 'Product updated successfully');
    // Log product update
    yield call(auditLogsAPI.createAuditLog, {
      entityType: 'product',
      entityId: id,
      changeType: 'update',
      oldData: oldProduct,
      newData: updatedProduct
    });
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to update product';
    yield put(updateProductFailure(errorMessage));
    message.error(errorMessage);
  }
}

function* getProductSaga(action) {
  try {
    const id = action.payload;
    const response = yield call(productsAPI.getProduct, id);
    const product = response.data.product || response.data;
    yield put(getProductSuccess(product));
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch product details';
    yield put(getProductFailure(errorMessage));
    message.error(errorMessage);
  }
}

export default function* productSaga() {
  yield takeLatest(types.FETCH_PRODUCTS_REQUEST, fetchProductsSaga);
  yield takeLatest(types.FETCH_PRODUCT_FORM_DATA_REQUEST, fetchProductFormDataSaga);
  yield takeLatest(types.CREATE_PRODUCT_REQUEST, createProductSaga);
  yield takeLatest(types.UPDATE_PRODUCT_REQUEST, updateProductSaga);
  yield takeLatest(types.GET_PRODUCT_REQUEST, getProductSaga);
}